import { Plane } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export default function Privacy() {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img 
                src="/logo-small.png" 
                alt="PetFlyCheck Logo" 
                className="w-12 h-12 object-contain"
              />
              <h1 className="text-2xl font-bold text-slate-900">PetFlyCheck</h1>
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-16">
        <h2 className="text-4xl font-bold text-slate-900 mb-8">Privacy Policy</h2>

        <Card>
          <CardContent className="p-8 prose prose-slate max-w-none">
            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">1. Information We Collect</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                We collect the following information to provide our service:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Email address (for receipt and sending verdict reports)</li>
                <li>Flight details (date, origin, destination, airline, transits)</li>
                <li>Pet travel crate photos (3 required photos)</li>
                <li>Pet travel documents (for Full Audit plan: passport, rabies vaccine, health certificate)</li>
                <li>Payment information (processed securely through Stripe)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">2. How We Use Your Information</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Your information is used solely to:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Perform AI-assisted analysis of your pet's travel crate and documents</li>
                <li>Provide you with a compliance verdict and recommendations</li>
                <li>Send payment receipts and analysis reports to your email</li>
                <li>Respond to support requests</li>
                <li>Improve our service quality</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">3. Data Storage & Retention</h3>
              <p className="text-slate-700 leading-relaxed">
                Uploaded photos and documents are stored securely and retained for a default period of 
                <strong> 30 days</strong> from the date of upload. After this period, files are automatically 
                deleted from our servers.
              </p>
              <p className="text-slate-700 leading-relaxed mt-3">
                Flight details, email addresses, and analysis results are stored for service provision and 
                record-keeping purposes.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">4. Data Sharing</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                We <strong>do not sell, rent, or share</strong> your personal information with third parties, except:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Stripe (payment processor) - for secure payment processing</li>
                <li>OpenAI (AI provider) - for image analysis (anonymized data)</li>
                <li>When required by law or legal process</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">5. Security</h3>
              <p className="text-slate-700 leading-relaxed">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-2">
                <li>Encrypted data transmission (HTTPS/TLS)</li>
                <li>Secure file storage with access controls</li>
                <li>Regular security audits</li>
                <li>Limited data access on a need-to-know basis</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">6. Your Rights</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Request access to your personal data</li>
                <li>Request deletion of your data (subject to legal retention requirements)</li>
                <li>Request correction of inaccurate data</li>
                <li>Withdraw consent for data processing (may limit service availability)</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-3">
                To exercise these rights, contact us at <strong>contact@petflycheck.io</strong>
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">7. Cookies</h3>
              <p className="text-slate-700 leading-relaxed">
                We use essential storage for the check itself (session, language, your cookie choice). We also use
                Google Analytics 4 and PostHog (site analytics), Google Ads conversion tags (to measure ad results), and,
                only if you accept in the cookie banner, Microsoft Clarity (anonymised session recordings). You can
                block these cookies in your browser settings at any time.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">8. Children's Privacy</h3>
              <p className="text-slate-700 leading-relaxed">
                Our service is not directed to individuals under 18. We do not knowingly collect data 
                from children.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">9. Changes to Privacy Policy</h3>
              <p className="text-slate-700 leading-relaxed">
                We may update this policy from time to time. Changes will be posted on this page with 
                an updated "Last Updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">10. Contact</h3>
              <p className="text-slate-700 leading-relaxed">
                For privacy-related questions or requests, contact us at:
                <br />
                <strong>contact@petflycheck.io</strong>
              </p>
            </section>

            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Last Updated:</strong> January 2026
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-slate-400">
            <p>© 2026 PetFlyCheck. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
